// FE L: extracts the inbound and outbound mapping that used to live
// inline in `ContactDetails.tsx`. Two reasons:
//
//   1. The mapping is the contract between the ContactDetails form state
//      (flat, German field names: `vorname`, `strasse`, `familienstand`)
//      and the API shape (nested + English: `firstName`,
//      `defaultAddress.street`, `maritalStatus`). Keeping it in one file
//      means the contract is one diff to review, one place to test.
//
//   2. The previous handleSave outbound block was a hand-maintained
//      mirror of the inbound block. Easy to forget a key. Co-locating
//      them makes that drift visible.
//
// Mirrors `contactRowMapper.ts` for the list view.

import { familienstandZumSpeichern } from "./familienstand";

export type ContactDetailApi = Record<string, any>;
export type ContactDetailForm = Record<string, any>;

/**
 * FE L: lightweight DTOs that replace `any[]` on the four state slots
 * that lift API responses straight into render — `anredeOptions`,
 * `titelOptions`, `empfehlungResults`, plus the active-public-forms
 * polling status. The shapes are intentionally narrow: just the fields
 * the form actually reads from the response.
 */
export interface SalutationOption {
  id: number;
  salutation: string | null;
}

export interface TitleOption {
  id: number;
  title: string | null;
}

export interface EmpfehlungResult {
  id: number;
  displayName?: string | null;
  lastname?: string | null;
  firstName?: string | null;
}

export type PublicFormStatus = 'NOT_FILLED' | 'FILLED';

interface RelationsSrcEntry {
  contactDest?: {
    id?: number;
    displayName?: string;
    lastname?: string;
    firstName?: string;
    erreichbarkeit?: string;
    communications?: Array<{
      id: number;
      type: string;
      label?: string;
      value: string;
    }>;
  };
  relationType?: { contactRelationType?: string };
}

export interface RelatedCommunication {
  contactId?: number;
  name: string;
  role: string;
  id: number;
  type: string;
  label?: string;
  value: string;
  erreichbarkeit?: string;
}

/**
 * API → form. Spreads the API payload and adds the German-aliased
 * shadow fields the inline form bindings read from. Also flattens
 * `defaultAddress` and `defaultBankAccount` into top-level shadow
 * fields (the form's `strasse` / `iban` inputs bind to flat paths).
 */
export function apiToForm(data: ContactDetailApi): ContactDetailForm {
  return {
    ...data,
    kontaktId: data.id,
    strasse: data.defaultAddress?.street,
    plz: data.defaultAddress?.postalCode,
    ort: data.defaultAddress?.city,
    ortsteil: data.defaultAddress?.ortsteil,
    land: data.defaultAddress?.country,
    relevanz: data.contactRelevance,
    value: data.contactValue,
    typ: data.type?.type,
    vorname: data.firstName ?? data.vorname,
    rufname: data.callName ?? '',
    geschlecht: data.gender ?? '',
    // RBAC ownership (read-only here; written via PUT /contact/:id/ownership).
    foUserId: data.foUserId ?? null,
    boType: data.boType ?? 'NONE',
    boUserId: data.boUserId ?? null,
    boTeamId: data.boTeamId ?? null,
    inhouseContactUserId: data.inhouseContactUserId ?? null,
    familienstand: data.maritalStatus,
    geburtsort: data.birthPlace,
    geburtsland: data.birthCountry,
    geburtsname: data.birthName,
    staatsang: data.nationality,
    berufsstatus: data.jobStatus,
    taetigkeit: data.jobOccupation,
    unternehmensfunktion: data.unternehmensfunktion,
    arbeitgeber: data.jobEmployer,
    mitgliedschaften: data.memberships,
    vorsteuerAbzugsberechtigt: data.taxDeductible,
    // Geschäftsjahr — EIN Feld auf financialYearEnd konsolidiert (Anlage + Detail).
    financialYearEnd: data.financialYearEnd,
    gruendungsdatum: data.gruendungsdatum,
    branche: data.jobIndustry,
    anrede: data.salutation?.id ? String(data.salutation.id) : undefined,
    titel: data.title?.id ? String(data.title.id) : undefined,
    iban: data.defaultBankAccount?.iban,
    bic: data.defaultBankAccount?.bic,
    kreditinstitut: data.defaultBankAccount?.bankName,
    accountHolder: data.defaultBankAccount?.accountHolder,
    directDebit: data.defaultBankAccount?.directDebit,
    relatedCommunications: flattenRelatedCommunications(data.relationsSrc),
    letztesJahresgespraech: data.letztesJahresgespraech
      ? String(data.letztesJahresgespraech).slice(0, 10)
      : undefined,
    empfehlungsgeber: data.empfehlungsgeber ?? undefined,
  };
}

/**
 * Flattens `relationsSrc[*].contactDest.communications[*]` into a flat
 * list keyed by (contactId, communication.id). Used by the "related
 * communications" panel which renders contacts of related people.
 */
export function flattenRelatedCommunications(
  relationsSrc: RelationsSrcEntry[] | undefined | null,
): RelatedCommunication[] {
  return (relationsSrc ?? []).flatMap((rel) => {
    const dest = rel.contactDest;
    const role = rel.relationType?.contactRelationType || 'Relation';
    if (!dest || !dest.communications) return [];
    return dest.communications.map((comm) => ({
      contactId: dest.id,
      name:
        dest.displayName ||
        `${dest.lastname ?? ''}, ${dest.firstName ?? ''}`.replace(/^,\s*|,\s*$/, ''),
      role,
      id: comm.id,
      type: comm.type,
      label: comm.label,
      value: comm.value,
      erreichbarkeit: dest.erreichbarkeit,
    }));
  });
}

/**
 * Form → API. Spreads the form state and renames the German
 * field names back to the API's English ones. Does NOT include
 * addresses / bankAccounts — the caller merges those in separately
 * via `mergeIntoDefault`. Salutation / title get re-nested as
 * `{ id }` refs.
 */
export function formToApi(form: ContactDetailForm): ContactDetailApi {
  // RBAC ownership fields are written exclusively via PUT /contact/:id/ownership
  // (the contact controller strips them from PUT/PATCH anyway). Drop the shadow
  // copies so the normal save never carries stale ownership.
  //
  // T1: `persoenlich` gehört in dieselbe Reihe. Es entsteht ausschließlich bei
  // der Anlage und ist danach unveränderlich; PUT und PATCH strippen es
  // serverseitig. Ginge es trotzdem mit, sähe der Feld-Diff des Speicherns
  // (`nurGeaenderteFelder`) eine Änderung, wo keine ist.
  const {
    foUserId: _fu,
    boType: _bt,
    boUserId: _bu,
    boTeamId: _btm,
    persoenlich: _pe,
    ...rest
  } = form;
  return {
    ...rest,
    gender: form.geschlecht,
    callName: form.rufname,
    // Der „Sonstiges“-Freitext trägt im Formular absichtlich Rand-Leerraum
    // (sonst ließe sich dort kein Leerzeichen tippen) — hier fällt er weg.
    maritalStatus: familienstandZumSpeichern(form.familienstand),
    nationality: form.staatsang,
    birthPlace: form.geburtsort,
    birthCountry: form.geburtsland,
    birthName: form.geburtsname,
    jobStatus: form.berufsstatus,
    jobOccupation: form.taetigkeit,
    unternehmensfunktion: form.unternehmensfunktion,
    jobEmployer: form.arbeitgeber,
    memberships: form.mitgliedschaften,
    taxDeductible: form.vorsteuerAbzugsberechtigt,
    financialYearEnd: form.financialYearEnd,
    gruendungsdatum: form.gruendungsdatum,
    jobIndustry: form.branche || form.berufssbranche,
    salutation: form.anrede ? { id: Number(form.anrede) } : null,
    title: form.titel ? { id: Number(form.titel) } : null,
  };
}
